import { AssessmentProgram } from "@lib/ShiOneClient";

const SPRV2_MAX = 3;
export const STANDARD_MAX = 5;

// Backend returns display names like "Security Posture Review v2" instead of enum values
export function normalizeAssessmentProgram(
  program: AssessmentProgram | string | undefined,
): AssessmentProgram {
  if (!program) return AssessmentProgram.Unknown;

  if (Object.values(AssessmentProgram).includes(program as AssessmentProgram)) {
    return program as AssessmentProgram;
  }

  if (typeof program === "string") {
    const stringMap: Record<string, AssessmentProgram> = {
      // Display names
      "Security Posture Review": AssessmentProgram.SecurityPostureReview,
      "Security Posture Review v2": AssessmentProgram.SecurityPostureReviewV2,
      "AI Readiness": AssessmentProgram.AiReadiness,
      "DevOps Posture Review": AssessmentProgram.DevOpsPostureReview,
      "AWS Maturity": AssessmentProgram.AwsMaturity,
      "Microsoft Adoption Assessment":
        AssessmentProgram.MicrosoftAdoptionAssessment,
      // URL slugs
      "security-posture-review": AssessmentProgram.SecurityPostureReview,
      "security-posture-review-v2": AssessmentProgram.SecurityPostureReviewV2,
      "ai-readiness": AssessmentProgram.AiReadiness,
      "dev-ops-posture-review": AssessmentProgram.DevOpsPostureReview,
      "aws-maturity-review": AssessmentProgram.AwsMaturity,
    };

    return stringMap[program] || AssessmentProgram.Unknown;
  }

  return AssessmentProgram.Unknown;
}

// Both SPR V1 and V2 display as "Security Posture Review"
export function getLabelFromProgram(
  program: AssessmentProgram | string | undefined,
): string {
  if (!program) return "";

  switch (program) {
    case AssessmentProgram.SecurityPostureReview:
    case AssessmentProgram.SecurityPostureReviewV2:
      return "Security Posture Review";
    case AssessmentProgram.AiReadiness:
      return "AI Readiness";
    case AssessmentProgram.MicrosoftAdoptionAssessment:
      return "Microsoft Adoption Assessment";
    case AssessmentProgram.DevOpsPostureReview:
      return "DevOps Posture Review";
    case AssessmentProgram.AwsMaturity:
      return "AWS Maturity";
    default:
      return program;
  }
}

export enum MaturityLevel {
  Unknown = "Unknown",
  Gap = "Gap",
  NeedsImprovement = "Needs Improvement",
  Operational = "Operational",
}

export function shouldUseTextLabels(
  program: AssessmentProgram | undefined,
): boolean {
  return program === AssessmentProgram.SecurityPostureReviewV2;
}

export function shouldHideTargetMaturity(
  program: AssessmentProgram | undefined,
): boolean {
  return program === AssessmentProgram.SecurityPostureReviewV2;
}

export function capMaturityScore(score: number | undefined | null): number {
  if (score === undefined || score === null || isNaN(score)) {
    return 0;
  }
  return Math.min(score, 4);
}

export function roundMaturityScore(score: number | undefined | null): number {
  if (score === undefined || score === null || isNaN(score)) {
    return 0;
  }
  return Math.round(capMaturityScore(score));
}

// Both 1 and 2 map to "Gap" to maintain 4 distinct categories
export function convertScoreToLabel(score: number | undefined | null): string {
  if (score === undefined || score === null || isNaN(score)) {
    return MaturityLevel.Unknown;
  }

  const roundedScore = roundMaturityScore(score);

  switch (roundedScore) {
    case 0:
      return MaturityLevel.Unknown;
    case 1:
    case 2:
      return MaturityLevel.Gap;
    case 3:
      return MaturityLevel.NeedsImprovement;
    case 4:
      return MaturityLevel.Operational;
    default:
      return MaturityLevel.Unknown;
  }
}

export function formatMaturityDisplay(
  score: number | undefined | null,
  program: AssessmentProgram | undefined,
): string {
  if (shouldUseTextLabels(program)) {
    return convertScoreToLabel(score);
  }
  return `${score ?? 0} / 5`;
}

export function getLabelMidpointValue(label: string): number {
  switch (label) {
    case MaturityLevel.Unknown:
      return 0;
    case MaturityLevel.Gap:
      return 1.5;
    case MaturityLevel.NeedsImprovement:
      return 3;
    case MaturityLevel.Operational:
      return 4;
    default:
      return 0;
  }
}

export function getLabelOrdinalPosition(label: string): number {
  switch (label) {
    case MaturityLevel.Unknown:
      return 0;
    case MaturityLevel.Gap:
      return 1;
    case MaturityLevel.NeedsImprovement:
      return 2;
    case MaturityLevel.Operational:
      return 3;
    default:
      return 0;
  }
}

// 0-4 scale has 5 values but only 4 unique labels
export function convertScoreToChartPosition(
  score: number | undefined | null,
): number {
  if (score === undefined || score === null || isNaN(score)) return 0;
  const clamped = Math.min(Math.max(score, 0), 4);
  if (clamped < 1) return clamped;
  if (clamped < 3) return 1 + (clamped - 1) / 2;
  return 2 + (clamped - 3);
}

export function convertChartPositionToLabel(position: number): string {
  const maturityLevels = getOrderedMaturityLevels();
  const index = Math.floor(position);
  if (index >= 0 && index < maturityLevels.length) {
    return maturityLevels[index];
  }
  return MaturityLevel.Unknown;
}

export function getOrderedMaturityLevels(): string[] {
  return [
    MaturityLevel.Unknown,
    MaturityLevel.Gap,
    MaturityLevel.NeedsImprovement,
    MaturityLevel.Operational,
  ];
}

export function formatMaturityChange(
  oldScore: number | undefined | null,
  newScore: number | undefined | null,
  program: AssessmentProgram | undefined,
): string {
  if (shouldUseTextLabels(program)) {
    const oldLabel = convertScoreToLabel(oldScore);
    const newLabel = convertScoreToLabel(newScore);

    if (oldLabel === newLabel) {
      return oldLabel;
    }

    return `${oldLabel} → ${newLabel}`;
  }

  const delta = getMaturityDelta(oldScore, newScore);
  return delta >= 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
}

export function getMaturityDelta(
  oldScore: number | undefined | null,
  newScore: number | undefined | null,
): number {
  const old = oldScore ?? 0;
  const current = newScore ?? 0;
  return current - old;
}

// Normalizes SPR V2 (0-3) to standard scale (0-5) so all assessments fill the chart proportionally
export function normalizeChartPositionToStandardScale(
  chartPosition: number | undefined | null,
): number {
  if (
    chartPosition === undefined ||
    chartPosition === null ||
    isNaN(chartPosition)
  ) {
    return 0;
  }

  return (chartPosition / SPRV2_MAX) * STANDARD_MAX;
}

// Converts normalized values back to SPR V2 ordinals for tooltip display
export function denormalizeStandardScaleToChartPosition(
  normalizedValue: number | undefined | null,
): number {
  if (
    normalizedValue === undefined ||
    normalizedValue === null ||
    isNaN(normalizedValue)
  ) {
    return 0;
  }

  return (normalizedValue / STANDARD_MAX) * SPRV2_MAX;
}

// Transforms backend maturity string labels to SPR V2 display labels
export function transformMaturityLabelForDisplay(
  backendLabel: string | undefined,
  program: AssessmentProgram | string | undefined,
): string {
  if (!backendLabel) return "Unknown";

  const normalizedProgram =
    typeof program === "string" ? normalizeAssessmentProgram(program) : program;

  if (shouldUseTextLabels(normalizedProgram)) {
    switch (backendLabel) {
      case "Functional":
        return "Needs Improvement";
      case "Evaluating":
        return "Gap";
      case "Installed":
        return "Operational";
      default:
        break;
    }
  }

  return backendLabel;
}
